import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const Analytics = () => {
  const location = useLocation();

  useEffect(() => {
    // Track page views for each route change
    const trackPageView = () => {
      // Microsoft Clarity is already initialized in App.js
      // Track additional custom events for SEO insights
      
      // Track recipe searches
      if (location.search && location.search.includes('q=')) {
        const searchTerm = new URLSearchParams(location.search).get('q');
        if (searchTerm && window.clarity) {
          window.clarity('event', 'recipe_search', { search_term: searchTerm });
        }
      }
      
      // Track recipe detail views
      if (location.pathname.includes('/recipe-details/')) {
        const recipeId = location.pathname.split('/recipe-details/')[1];
        if (recipeId && window.clarity) {
          window.clarity('event', 'recipe_view', { recipe_id: recipeId });
        }
      }
      
      // Track content creator views
      if (location.pathname.includes('/content-creator/')) {
        const creatorId = location.pathname.split('/content-creator/')[1];
        if (creatorId && window.clarity) {
          window.clarity('event', 'creator_view', { creator_id: creatorId });
        }
      }
      
      // Track recipe uploads
      if (location.pathname === '/recipe-add') {
        if (window.clarity) {
          window.clarity('event', 'recipe_add_page_view');
        }
      }
      
      // Track advanced search usage
      if (location.pathname === '/advanced-search') {
        if (window.clarity) {
          window.clarity('event', 'advanced_search_page_view');
        }
      }
    };

    // Track Core Web Vitals for SEO performance
    const trackWebVitals = () => {
      // Track Time to First Byte (TTFB)
      if (window.performance && window.performance.timing) {
        const ttfb = window.performance.timing.responseStart - window.performance.timing.navigationStart;
        if (window.clarity && ttfb > 0) {
          window.clarity('event', 'performance_ttfb', { value: ttfb });
        }
      }
      
      // Track Largest Contentful Paint (LCP) when available
      if ('PerformanceObserver' in window) {
        try {
          const observer = new PerformanceObserver((list) => {
            const entries = list.getEntries();
            const lastEntry = entries[entries.length - 1];
            if (lastEntry && window.clarity) {
              window.clarity('event', 'performance_lcp', { value: Math.round(lastEntry.startTime) });
            }
          });
          observer.observe({ entryTypes: ['largest-contentful-paint'] });
        } catch (error) {
          console.log('LCP tracking not supported');
        }
      }
    };

    // Execute tracking
    trackPageView();
    trackWebVitals();
    
    // Track scroll depth for engagement metrics
    let maxScrollDepth = 0;
    const trackScrollDepth = () => {
      const scrollDepth = Math.round((window.scrollY / (document.body.scrollHeight - window.innerHeight)) * 100);
      if (scrollDepth > maxScrollDepth) {
        maxScrollDepth = scrollDepth;
        if (scrollDepth >= 25 && scrollDepth < 50 && window.clarity) {
          window.clarity('event', 'scroll_25_percent');
        } else if (scrollDepth >= 50 && scrollDepth < 75 && window.clarity) {
          window.clarity('event', 'scroll_50_percent');
        } else if (scrollDepth >= 75 && scrollDepth < 90 && window.clarity) {
          window.clarity('event', 'scroll_75_percent');
        } else if (scrollDepth >= 90 && window.clarity) {
          window.clarity('event', 'scroll_90_percent');
        }
      }
    };

    // Add scroll listener
    window.addEventListener('scroll', trackScrollDepth);
    
    // Cleanup
    return () => {
      window.removeEventListener('scroll', trackScrollDepth);
    };
  }, [location]);

  return null; // This component doesn't render anything
};

export default Analytics; 